import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Row, Col, Button, Modal, Select } from 'antd';
import { useDispatch } from 'react-redux';
import FloatingLabel from '../../../../components/FloatingLabel/FloatingLabel';
import SelectDropdown from '../../../../components/SelectDropdown/SelectDropdown';
import { UploadField } from '../../../../components/SharedComponents/FormComponents/UploadField';
import { CloseIcon, ChevronDown, ChevronUp } from '../../../../assets/icons';
import { alphabetSpaceRegex, einRegex } from '../../../../utils/Validations';
import {
  ENTITY_NAME_LENGTHS,
  ENTITY_NAME_VALIDATION,
  ENTITY_TYPE_VALIDATION,
  STATE_OF_INCORPORATION_LENGTHS,
  STATE_OF_INCORPORATION_VALIDATION,
  EIN_VALIDATION,
} from '../../../../utils/validationMessages';
import {
  ENTITY_TYPE_OPTIONS,
  ENTITY_DOCUMENT_TYPE_OPTIONS,
  ENTITY_DOC_DUPLICATE_TOAST_KEY,
  ENTITY_DOC_LIMIT_TOAST_KEY,
  MAX_ENTITY_DOCUMENTS,
} from '../../../../constants/accountType.constants';
import { ENTITY_DOC_UPLOAD_VALIDATION } from '../../../../utils/fileValidation';
import { getEntityDocLimitMessage, selectEntityDocuments } from '../../../../utils/entityDocumentSelection';
import { ENTITY_DOC_UPLOAD_STATUS, uploadEntityDocuments } from '../../../../utils/entityDocumentUpload';
import { isPasswordProtectedPdf } from '../../../../utils/pdfPasswordCheck';
import EntityDocumentItem from '../../../../components/EntityDocumentItem/EntityDocumentItem';
import { decryptEIN, maskEIN, formatEIN } from '../../../../utils/einDecryption';
import { updateUserProfile } from '../../../../services/authApi/userApi';
import { addUserData } from '../../../../store/reducer/accountSlice';
import { toastNotify } from '../../../../utils/toastNotify';

const EditEntityDetails = (props) => {
  const { entityProfile } = props;
  const [entityName, setEntityName] = useState();
  const [entityType, setEntityType] = useState();
  const [stateOfIncorporation, setStateOfIncorporation] = useState();
  const [isEinEditing, setIsEinEditing] = useState(false);
  const [ein, setEin] = useState();
  const [docType, setDocType] = useState(ENTITY_DOCUMENT_TYPE_OPTIONS[0]);
  const [arrowUp, setArrowUp] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [addedDocuments, setAddedDocuments] = useState([]);
  // Per-document upload progress keyed by uid: { state, percent }.
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [fileValidate, setFileValidate] = useState(false);
  const [fileSizeValidate, setFileSizeValidate] = useState(false);
  const [passwordProtectedValidate, setPasswordProtectedValidate] = useState(false);
  const latestSelectionRef = useRef(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [isEdit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChangedValue, setIsChangedValue] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const einInputRef = useRef(null);

  const decryptedEin = decryptEIN(entityProfile?.encodedEIN);
  const einMasked = maskEIN(decryptedEin);
  // EIN is optional — clearing a previously-set value is itself a valid change, so this must
  // not require `ein` to be non-empty (that previously left Save disabled after clearing it).
  const isEinChanged = isEinEditing && ein !== decryptedEin;

  useEffect(() => {
    handleSetDefaultData();
  }, [entityProfile]);

  // Focus the EIN input as soon as it's revealed by "Change".
  useEffect(() => {
    if (isEinEditing) {
      einInputRef.current?.focus();
    }
  }, [isEinEditing]);

  const handleSetDefaultData = () => {
    if (entityProfile && Object.keys(entityProfile).length > 0) {
      form.setFieldsValue({
        entityName: entityProfile?.entityName,
        entityType: entityProfile?.entityType,
        stateOfIncorporation: entityProfile?.stateOfIncorporation,
      });
      setEntityName(entityProfile?.entityName);
      setEntityType(entityProfile?.entityType);
      setStateOfIncorporation(entityProfile?.stateOfIncorporation);
      setIsEinEditing(false);
      setEin(undefined);
      setDocType(ENTITY_DOCUMENT_TYPE_OPTIONS[0]);
      setFileList([]);
      setAddedDocuments([]);
      setFileValidate(false);
      setFileSizeValidate(false);
      setPasswordProtectedValidate(false);
      setEdit(true);
    }
  };

  const handleEinInput = (e) => {
    const formattedEIN = formatEIN(e.target.value);
    form.setFieldValue('ein', formattedEIN);
    setEin(formattedEIN);
    // Form's onFieldsChange only reconciles this field on blur (validateTrigger='onBlur'),
    // so re-check changes here directly with the fresh value instead of waiting for that.
    handleCheckChanges(form.getFieldsValue(), fileList, addedDocuments, formattedEIN);
  };

  const startEinEditing = () => {
    // entityProfile.encodedEIN is an encrypted value from the API — decrypt it so the
    // input can be pre-filled with the real EIN instead of opening blank.
    form.setFieldValue('ein', decryptedEin);
    setEin(decryptedEin);
    setIsEinEditing(true);
  };

  const handleCheckChanges = (values, currentFileList = fileList, currentDocuments = addedDocuments, currentEin = ein) => {
    const einChangedNow = isEinEditing && currentEin !== decryptedEin;
    const changed =
      values?.entityName !== entityProfile?.entityName ||
      values?.entityType !== entityProfile?.entityType ||
      values?.stateOfIncorporation !== entityProfile?.stateOfIncorporation ||
      einChangedNow ||
      currentFileList.length > 0 ||
      currentDocuments.length > 0;
    setIsChangedValue(changed);
  };

  const onChangeHandle = async ({ fileList: newFileList }) => {
    const selectionId = latestSelectionRef.current + 1;
    latestSelectionRef.current = selectionId;

    if (!newFileList.length) {
      setFileList([]);
      handleCheckChanges(form.getFieldsValue(), [], addedDocuments);
      return;
    }

    const remainingSlots = MAX_ENTITY_DOCUMENTS - addedDocuments.length;
    if (remainingSlots <= 0) {
      toastNotify('info', `You can only add up to ${MAX_ENTITY_DOCUMENTS} documents.`, ENTITY_DOC_LIMIT_TOAST_KEY);
      setFileList([]);
      return;
    }

    // Drop password-protected PDFs before taking slots, so a locked file doesn't use one up.
    const passwordChecks = await Promise.all(newFileList.map((item) => isPasswordProtectedPdf(item?.originFileObj)));
    // AntD calls onChange once per selected file with a growing list; only the latest call
    // (which holds every selected file) is applied, whatever order the async checks finish in.
    if (selectionId !== latestSelectionRef.current) return;
    const selection = selectEntityDocuments({ fileList: newFileList, passwordChecks, addedDocuments, docType });

    setPasswordProtectedValidate(selection.hasPasswordProtected);
    setFileValidate(selection.hasInvalidFormat);
    setFileSizeValidate(selection.hasInvalidSize);

    if (selection.skippedForLimit.length) {
      // AntD calls onChange once per selected file, so key the toast to show it only once per selection.
      toastNotify('info', getEntityDocLimitMessage(selection.skippedForLimit), ENTITY_DOC_LIMIT_TOAST_KEY);
    }

    if (selection.hasDuplicate) {
      toastNotify('info', 'This document has already been added.', ENTITY_DOC_DUPLICATE_TOAST_KEY);
    }
    const nextDocuments = [...addedDocuments, ...selection.documentsToAdd];

    setAddedDocuments(nextDocuments);
    setFileList([]);
    handleCheckChanges(form.getFieldsValue(), [], nextDocuments);
  };

  const beforeUpload = () => false;

  const handleRemoveUploaded = (fileId) => {
    const updatedFileList = fileList.filter((item) => item.uid !== fileId);
    setFileList(updatedFileList);
    setFileValidate(false);
    setFileSizeValidate(false);
    setPasswordProtectedValidate(false);
    handleCheckChanges(form.getFieldsValue(), updatedFileList, addedDocuments);
  };

  const handlePreviewDocument = (documentItem) => {
    if (!documentItem?.originFileObj) {
      return;
    }

    const previewUrl = URL.createObjectURL(documentItem.originFileObj);
    setPreviewDocument({
      ...documentItem,
      previewUrl,
      mimeType: documentItem.originFileObj.type,
    });
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    if (previewDocument?.previewUrl) {
      URL.revokeObjectURL(previewDocument.previewUrl);
    }
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  const handleRemoveAddedDocument = (fileId) => {
    const nextDocuments = addedDocuments.filter((item) => item.uid !== fileId);
    setAddedDocuments(nextDocuments);
    setIsChangedValue(
      form.getFieldValue('entityName') !== entityProfile?.entityName ||
        form.getFieldValue('entityType') !== entityProfile?.entityType ||
        form.getFieldValue('stateOfIncorporation') !== entityProfile?.stateOfIncorporation ||
        isEinChanged ||
        fileList.length > 0 ||
        nextDocuments.length > 0,
    );
  };

  const isUploadInvalid = fileValidate || fileSizeValidate;

  const handleDocumentStatusChange = (uid, status) => {
    setUploadStatuses((prevStatuses) => ({ ...prevStatuses, [uid]: status }));
  };

  // Marks every document as Waiting, then uploads them one by one so each card shows its own progress.
  const uploadDocumentsWithProgress = async () => {
    setUploadStatuses(Object.fromEntries(addedDocuments.map((item) => [item.uid, { state: ENTITY_DOC_UPLOAD_STATUS.WAITING }])));
    setIsUploadingDocs(true);
    try {
      return await uploadEntityDocuments(addedDocuments, handleDocumentStatusChange);
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const onEditEntitySubmit = async (values) => {
    setLoading(true);
    try {
      const entityFormData = {
        entityName: values.entityName?.trim(),
        entityType,
        stateOfIncorporation: values.stateOfIncorporation?.trim(),
      };
      // EIN is optional — only send it when the user actually entered a value; an empty
      // field is omitted from the payload rather than sent as a blank/cleared ein.
      if (isEinChanged && ein) {
        entityFormData.ein = ein;
      }

      if (addedDocuments.length) {
        entityFormData.entityDocs = await uploadDocumentsWithProgress();
      }

      const resp = await updateUserProfile(entityFormData);
      if (resp.isError) {
        toastNotify('error', resp?.message || 'Failed to update entity information. Please try again.');
      } else {
        dispatch(addUserData({ userData: resp?.data?.user }));
        props.setClose();
        toastNotify('success', 'Entity information updated successfully.');
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message || 'Failed to update entity information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className='account__modal'
      open={props.setOpen}
      onCancel={() => {
        handleSetDefaultData();
        props.setClose();
      }}
      width={524}
      footer={false}
      closeIcon={<CloseIcon />}
      centered
    >
      <h4 className='mb-32 account__modal__title'>Edit entity information</h4>
      <Form
        onFieldsChange={() => handleCheckChanges(form.getFieldsValue())}
        name='editEntityDetailsForm'
        form={form}
        onFinish={onEditEntitySubmit}
        autoComplete='off'
      >
        <FloatingLabel label='Entity name' inputId='entityName' inputValue={entityName} isEdit={isEdit}>
          <Form.Item
            validateTrigger='onBlur'
            name='entityName'
            rules={[
              { required: true, message: ENTITY_NAME_VALIDATION.REQUIRED },
              { max: ENTITY_NAME_LENGTHS.MAX, message: ENTITY_NAME_VALIDATION.MAX_LENGTH },
              { pattern: /^\S/, message: ENTITY_NAME_VALIDATION.NO_LEADING_SPACE },
            ]}
          >
            <Input id='entityName' value={entityName} onChange={(e) => setEntityName(e.target.value)} />
          </Form.Item>
        </FloatingLabel>
        <SelectDropdown
          label='Entity type'
          name='entityType'
          inputId='entityType'
          id='entityType'
          initialValue={entityType}
          options={ENTITY_TYPE_OPTIONS}
          onChangeSelect={(_, value) => setEntityType(value)}
          rules={[{ required: true, message: ENTITY_TYPE_VALIDATION.REQUIRED }]}
        />
        <div className='mb-24'>
          {addedDocuments.length < MAX_ENTITY_DOCUMENTS && (
            <>
              <div id='entity-doc-type-anchor' />
              <label htmlFor='entityDocType' className='entitydocs__label'>
                Type of document
              </label>
              <Form.Item name='docType' initialValue={docType}>
                <Select
                  id='entityDocType'
                  onChange={(value) => setDocType(value)}
                  value={docType}
                  getPopupContainer={() => document.getElementById('entity-doc-type-anchor')}
                  onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                  suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                >
                  {ENTITY_DOCUMENT_TYPE_OPTIONS.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item className='entity-doc'>
                <UploadField
                  onChangeHandle={onChangeHandle}
                  fileList={fileList}
                  id='entityDoc'
                  beforeUpload={beforeUpload}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileList[0].uid)}
                  label='Upload document'
                  fileValidate={fileValidate}
                  fileSizeValidate={fileSizeValidate}
                  passwordProtectedValidate={passwordProtectedValidate}
                  loading={loading}
                  accept='image/png,image/jpeg,image/jpg,application/pdf'
                  validationMessages={ENTITY_DOC_UPLOAD_VALIDATION}
                  variant='dropzone'
                  multiple
                  hint='Accepted file types: JPG, JPEG, PNG, PDF • Max file size: 10MB per file'
                />
              </Form.Item>
            </>
          )}
          {addedDocuments.length > 0 && (
            <div className='entitydocs__added-list'>
              <p className='fw-600 mb-8'>Added documents</p>
              {addedDocuments.map((documentItem) => (
                <EntityDocumentItem
                  key={documentItem.uid}
                  documentItem={documentItem}
                  uploadStatus={uploadStatuses[documentItem.uid]}
                  isUploading={isUploadingDocs || (loading && !!uploadStatuses[documentItem.uid])}
                  onPreview={() => handlePreviewDocument(documentItem)}
                  onRemove={() => handleRemoveAddedDocument(documentItem.uid)}
                />
              ))}
            </div>
          )}
          {addedDocuments.length >= MAX_ENTITY_DOCUMENTS && (
            <p className='entitydocs__limit-note mb-0'>You have reached the {MAX_ENTITY_DOCUMENTS} document maximum.</p>
          )}
        </div>
        <FloatingLabel label='State of incorporation/formation' inputId='stateOfIncorporation' inputValue={stateOfIncorporation} isEdit={isEdit}>
          <Form.Item
            validateTrigger='onBlur'
            name='stateOfIncorporation'
            rules={[
              { required: true, message: STATE_OF_INCORPORATION_VALIDATION.REQUIRED },
              {
                transform: (value) => value?.trim(),
                max: STATE_OF_INCORPORATION_LENGTHS.MAX,
                message: STATE_OF_INCORPORATION_VALIDATION.MAX_LENGTH,
              },
              {
                transform: (value) => value?.trim(),
                min: STATE_OF_INCORPORATION_LENGTHS.MIN,
                message: STATE_OF_INCORPORATION_VALIDATION.MIN_LENGTH,
              },
              { pattern: alphabetSpaceRegex, message: STATE_OF_INCORPORATION_VALIDATION.FORMAT },
            ]}
          >
            <Input id='stateOfIncorporation' value={stateOfIncorporation} onChange={(e) => setStateOfIncorporation(e.target.value)} />
          </Form.Item>
        </FloatingLabel>
        <div className='mb-24'>
          <p className='data__field__name mb-4'>Employer Identification Number</p>
          {isEinEditing ? (
            <FloatingLabel label='EIN' inputId='ein' inputValue={ein}>
              <Form.Item validateTrigger='onBlur' name='ein' rules={[{ pattern: einRegex, message: EIN_VALIDATION.FORMAT }]}>
                <Input id='ein' maxLength={10} placeholder={einMasked || 'XX-XXXXXXX'} value={ein} onChange={handleEinInput} ref={einInputRef} />
              </Form.Item>
            </FloatingLabel>
          ) : (
            <p className='data__field__value'>
              {einMasked || 'N/A'}{' '}
              <button type='button' className='link-button text-terracotta underline cursor-pointer fz-14' onClick={startEinEditing}>
                Change
              </button>
            </p>
          )}
        </div>
        <Row gutter={[16, 16]}>
          <Col span={24} sm={12}>
            <Form.Item className='mb-0'>
              <Button
                className='w-100'
                onClick={() => {
                  props.setClose();
                  handleSetDefaultData();
                }}
              >
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col span={24} sm={12}>
            <Form.Item className='mb-0' shouldUpdate>
              {() => (
                <Button
                  className='w-100'
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  disabled={form.getFieldsError().some(({ errors }) => errors.length) || !isChangedValue || isUploadInvalid}
                >
                  Save
                </Button>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Modal open={previewOpen} onCancel={handleClosePreview} footer={null} width={900} centered title={previewDocument?.fileName}>
        <div style={{ height: '70vh', overflow: 'auto' }}>
          {previewDocument?.mimeType === 'application/pdf' ? (
            <iframe
              src={previewDocument?.previewUrl}
              title={previewDocument?.fileName}
              style={{ width: '100%', height: '100%', minHeight: '65vh', border: 0 }}
            />
          ) : (
            <img
              src={previewDocument?.previewUrl}
              alt={previewDocument?.fileName}
              style={{ width: '100%', maxHeight: '65vh', objectFit: 'contain' }}
            />
          )}
        </div>
      </Modal>
    </Modal>
  );
};

EditEntityDetails.propTypes = {
  entityProfile: PropTypes.shape({
    entityName: PropTypes.string,
    entityType: PropTypes.string,
    stateOfIncorporation: PropTypes.string,
    encodedEIN: PropTypes.string,
  }),
  setOpen: PropTypes.bool,
  setClose: PropTypes.func,
};

export default EditEntityDetails;
